import { useEffect, useRef, useState } from 'preact/hooks';
import type { MomentaryButtonWidget } from '../widgets';
import type { IPointBindingSource, WriteAcknowledgement } from '../binding';
import { showConfirm } from '../../components/Confirmer';
import './MomentaryButtonRenderer.css';

interface Props {
  widget: MomentaryButtonWidget;
  source: IPointBindingSource;
}

/**
 * Impuls-Button: writes `pulseValue`, waits `pulseDurationMs`, writes
 * `releaseValue` (defaulting to false/0 when omitted). The pending marker
 * covers the whole round-trip: the button stays disabled and shows a spinner
 * ring until BOTH writes have terminated. A rejection on either write flips
 * the ring red and surfaces the "Regler hat den Wert verworfen" copy from
 * the concept.
 *
 * Concept §3 note: this widget does not consume feedback because the pulse
 * is edge-triggered and usually shorter than the feedback polling interval.
 * The renderer therefore never dims — there is no feedback quality to check.
 */
export function MomentaryButtonRenderer({ widget, source }: Props) {
  const [pending, setPending] = useState<'idle' | 'sending' | 'rejected'>('idle');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const label = widget.label ?? 'Impuls';
  const durationMs = widget.pulseDurationMs ?? 500;

  async function onClick() {
    if (pending === 'sending') return;
    if (widget.confirmBeforeWrite) {
      const ok = await showConfirm({
        title: label,
        message: 'Impuls wirklich auslösen?',
        confirmLabel: 'Auslösen',
        danger: true,
      });
      if (!ok) return;
    }
    setPending('sending');
    const ac = new AbortController();
    abortRef.current = ac;
    try {
      const pulseOk = await drainWrite(source, widget.commandPoint, widget.pulseValue, ac.signal);
      if (!pulseOk) {
        setPending('rejected');
        return;
      }
      await wait(durationMs, ac.signal);
      if (ac.signal.aborted) return;
      const releaseValue = widget.releaseValue !== undefined
        ? widget.releaseValue
        : coerceReleaseDefault(widget.pulseValue);
      const releaseOk = await drainWrite(source, widget.commandPoint, releaseValue, ac.signal);
      if (!releaseOk) {
        setPending('rejected');
        return;
      }
      setPending('idle');
    } catch {
      if (!ac.signal.aborted) setPending('idle');
    } finally {
      if (abortRef.current === ac) abortRef.current = null;
    }
  }

  return (
    <div class={`hv-momentary hv-momentary--${pending}`}>
      {widget.label && <div class="hv-momentary__label">{widget.label}</div>}
      <button
        type="button"
        class="hv-momentary__btn"
        onClick={onClick}
        disabled={pending === 'sending'}
        aria-busy={pending === 'sending'}
      >
        {pending === 'sending' && <span class="hv-momentary__spinner" aria-hidden="true" />}
        <span class="hv-momentary__caption">{label}</span>
      </button>
      {pending === 'sending' && (
        <div class="hv-momentary__status" role="status">
          … gesendet, warte auf Regler
        </div>
      )}
      {pending === 'rejected' && (
        <div class="hv-momentary__status hv-momentary__status--rejected" role="status">
          Regler hat den Wert verworfen
        </div>
      )}
    </div>
  );
}

/**
 * Drain the write ack-stream to a terminal state. Returns true on confirmed,
 * false on rejected. Aborts propagate as a thrown AbortError; the caller is
 * responsible for treating that as a silent unmount.
 */
async function drainWrite(
  source: IPointBindingSource,
  pointId: string,
  value: unknown,
  signal: AbortSignal,
): Promise<boolean> {
  let terminal: WriteAcknowledgement | null = null;
  for await (const ack of source.write(pointId, value, signal)) {
    if (ack.state === 'confirmed' || ack.state === 'rejected') {
      terminal = ack;
      break;
    }
  }
  return terminal?.state === 'confirmed';
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => resolve(), ms);
    signal.addEventListener('abort', () => {
      window.clearTimeout(t);
      reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
  });
}

/**
 * Pick the release value when the widget didn't declare one. Bool pulses
 * release to false; numeric pulses release to 0; anything else stays at
 * whatever the pulse value was (no safer default without a type hint).
 */
function coerceReleaseDefault(pulseValue: unknown): unknown {
  if (typeof pulseValue === 'boolean') return false;
  if (typeof pulseValue === 'number') return 0;
  return pulseValue;
}
